import gsap from 'gsap';
import { clamp, createCell, getUrl, hypotenuse, log, random } from 'helpers';
import * as PIXI from 'pixi.js';
import * as setup from 'setup';
import Entity from '../Entity/Entity';
import Debug from './Debug/Debug';
import Fog from './Fog/Fog';

export default class Map extends PIXI.Container {
    app: PIXI.Application;
    debug: Debug;
    drag: {
        position: PIXI.Point;
        mouse: PIXI.Point;
    };
    entities: PIXI.Container;
    fog: Fog;
    followingEntity?: Entity | null;
    gameState: GameState;
    indicators: PIXI.Container<PIXI.Text>;
    zoom: number;

    sidebarWidth = 500 - 16;
    view = {
        bottom: setup.map.cells.wide - 1,
        center: createCell(),
        left: 0,
        radius: 0,
        right: setup.map.cells.wide - 1,
        top: 0,
        zoom: 0,
    };

    constructor(app: PIXI.Application, gameState: GameState) {
        super();

        // Initialize
        this.app = app;
        this.gameState = gameState;
        this.eventMode = 'static';
        this.zoom = setup.map.zoom.start;

        // Add terrain (pixi v8 loads textures via the Assets system, so create
        // the sprite up front and assign the texture once it has resolved)
        const terrain = new PIXI.Sprite();
        this.addChild(terrain);
        void PIXI.Assets.load<PIXI.Texture>(getUrl('/assets/map/map-terrain.png')).then(texture => {
            if (!terrain.destroyed) {
                terrain.texture = texture;
            }
        });

        // Add entities
        this.entities = new PIXI.Container();
        this.addChild(this.entities);
        this.entities.sortableChildren = true;

        // Add mask
        const maskSize = setup.map.cells.size * setup.map.cells.wide;
        const mask = new PIXI.Graphics();
        this.addChild(mask);
        this.entities.mask = mask;
        mask.rect(0, 0, maskSize, maskSize);
        mask.fill(0);

        // Add indicators
        this.indicators = new PIXI.Container();
        this.addChild(this.indicators);

        // Add fog
        this.fog = new Fog(gameState);
        this.addChild(this.fog);

        // Add debug
        this.debug = new Debug(gameState);
        this.addChild(this.debug);

        // Add listeners
        this.addListener('removed', this.handleRemoved);
        this.addListener('mouseover', this.handleHover);
        this.addListener('mouseout', this.handleHover);
        this.addListener('mousedown', this.handleDragStart);
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('mousewheel', this.handleMouseWheel, { passive: false });
        window.addEventListener('resize', this.handleResize);
    }

    debugLog = (message: string) => {
        if (this.gameState.settings.debugMap) {
            log(this.gameState.turns, 'Map', message);
            return true;
        }
        return false;
    };

    updateGameState = (gameState: GameState) => {
        this.gameState = gameState;
        this.debug.updateGameState(gameState);
        this.fog.updateGameState(gameState);
    };

    handleRemoved = () => {
        // Kill tweens
        gsap.killTweensOf(this.scale);
        gsap.killTweensOf(this.position);
        this.indicators.children.forEach(indicator => gsap.killTweensOf(indicator));

        // Remove listeners
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('mousewheel', this.handleMouseWheel);
        window.removeEventListener('resize', this.handleResize);
    };

    handleResize = () => {
        setTimeout(this.resize, 100);
    };

    resize = () => {
        const { cells, size, startArea, zoom } = setup.map;
        const { debugMap } = this.gameState.settings;
        const view = this.app.canvas;

        // Set zoom
        const zoomSize = debugMap ? size : startArea.width * cells.size;
        const zoomAmount = this.clampZoom(
            (Math.min(view.clientWidth - this.sidebarWidth, view.clientHeight) / zoomSize) *
                (debugMap ? 0.95 : zoom.start)
        );
        this.setZoom(zoomAmount);

        // Set center position
        const centerPosition = this.getCenterPosition(
            startArea.x + startArea.width / 2,
            startArea.y + startArea.height / 2
        );
        this.x = centerPosition.x;
        this.y = centerPosition.y;

        // Update view
        this.updateView();
    };

    handleHover = (e: PIXI.FederatedPointerEvent) => {
        document.body.style.cursor = e.type === 'mouseover' ? 'grab' : 'default';
    };

    handleDragStart = (e: PIXI.FederatedPointerEvent) => {
        // Unfollow previously following entity
        this.unfollowEntity();

        // Store initial data
        this.drag = {
            position: this.position.clone(),
            mouse: e.global.clone(),
        };

        // Chance cursor
        document.body.style.cursor = 'grabbing';

        // Add listeners
        this.addListener('mousemove', this.handleDragMove);
        this.addListener('mouseupoutside', this.handleDragEnd);
        this.addListener('mouseup', this.handleDragEnd);
    };

    handleDragMove = (e: PIXI.FederatedPointerEvent) => {
        const { mouse, position } = this.drag;

        // Update position
        this.x = position.x + (e.global.x - mouse.x);
        this.y = position.y + (e.global.y - mouse.y);

        // Update view
        this.updateView();
    };

    handleDragEnd = () => {
        // Chance cursor
        document.body.style.cursor = 'grab';

        // Remove listeners
        this.removeListener('mousemove', this.handleDragMove);
        this.removeListener('mouseupoutside', this.handleDragEnd);
        this.removeListener('mouseup', this.handleDragEnd);

        // Update view
        this.updateView();
    };

    handleKeyDown = (e: KeyboardEvent) => {
        const { cells, pan, zoom } = setup.map;

        const num = parseInt(e.key);
        if (!isNaN(num)) {
            // Change speed
            this.gameState.setSpeed(num);
        } else if (e.key === '-' || e.key === '_' || e.key === '+' || e.key === '=') {
            // Change zoom
            this.changeZoom(e.key === '-' || e.key === '_', zoom.scrollStep);
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            // Change position
            const panAmount =
                cells.size *
                this.zoom *
                pan.step *
                (e.altKey ? pan.altMultiplier : 1) *
                (e.shiftKey ? pan.shiftMultiplier : 1);
            switch (e.key) {
                case 'ArrowLeft':
                    this.x += panAmount;
                    break;
                case 'ArrowRight':
                    this.x -= panAmount;
                    break;
                case 'ArrowUp':
                    this.y += panAmount;
                    break;
                case 'ArrowDown':
                    this.y -= panAmount;
                    break;
            }
        }
    };

    handleMouseWheel = (e: any) => {
        if (e.target?.tagName.toLowerCase() === 'canvas') {
            e.preventDefault();
            const { altMultiplier, scrollStep, shiftMultiplier } = setup.map.zoom;

            // Get pointer on canvas
            const pointer = createCell();
            pointer.x = (e.offsetX - this.sidebarWidth) / (e.target.clientWidth - this.sidebarWidth);
            pointer.y = e.offsetY / e.target.clientHeight;

            // Change zoom
            const increase = e.deltaX > 0 || e.deltaY > 0;
            const step = scrollStep * (e.altKey ? altMultiplier : 1) * (e.shiftKey ? shiftMultiplier : 1);
            this.changeZoom(increase, step, pointer);
        }
    };

    centerPosition = (point: PIXI.Point, zoom?: number) => {
        // Set position
        const position = this.getCenterPosition(point.x / setup.map.cells.size, point.y / setup.map.cells.size, zoom);
        this.x = position.x;
        this.y = position.y;

        // Update view
        this.updateView();
    };

    getCenterPosition = (x: number, y: number, zoom?: number) => {
        // Get scale
        const scale = zoom ? this.clampZoom(zoom) : this.zoom;

        // Get position
        const position = createCell();
        position.x =
            (this.app.canvas.clientWidth - this.sidebarWidth) / 2 - x * scale * setup.map.cells.size + this.sidebarWidth;
        position.y = this.app.canvas.clientHeight / 2 - y * scale * setup.map.cells.size;
        return position;
    };

    clampZoom = (zoom: number) => {
        return clamp(zoom, setup.map.zoom.min, setup.map.zoom.max);
    };

    changeZoom = (increase: boolean, step: number, pointer?: PIXI.Point) => {
        // Check pointer
        if (!pointer) {
            pointer = createCell(0.5, 0.5);
        }
        pointer.x = pointer.x * (this.app.canvas.width - this.sidebarWidth) + this.sidebarWidth;
        pointer.y = pointer.y * this.app.canvas.height;

        // Get point on map on pointer position
        const point = createCell();
        point.x = (pointer.x - this.x) / this.width;
        point.y = (pointer.y - this.y) / this.height;

        // Set zoom
        this.setZoom((increase ? -step : step) + this.zoom);

        // Offset position to pointer
        this.x = pointer.x - this.width * point.x;
        this.y = pointer.y - this.height * point.y;

        // Update view
        this.updateView();
    };

    setZoom = (zoom: number) => {
        // Set zoom
        this.zoom = this.clampZoom(zoom);
        this.scale.set(this.zoom);

        // Run updates
        this.updateView();
    };

    updateView = () => {
        const { cells, zoom } = setup.map;
        const view = this.app.canvas;
        this.view.bottom = clamp(
            Math.ceil(cells.wide + (view.clientHeight - this.y - this.height) / this.zoom / cells.size),
            0,
            cells.wide - 1
        );
        this.view.left = clamp(Math.floor(-this.x / this.zoom / cells.size), 0, cells.wide - 1);
        this.view.right = clamp(
            Math.ceil(cells.wide + (view.clientWidth - this.x - this.width) / this.zoom / cells.size),
            0,
            cells.wide - 1
        );
        this.view.top = clamp(Math.floor(-this.y / this.zoom / cells.size), 0, cells.wide - 1);
        this.view.center.x = Math.round((this.view.right - this.view.left) / 2 + this.view.left);
        this.view.center.y = Math.round((this.view.bottom - this.view.top) / 2 + this.view.top);
        this.view.radius = Math.max(this.view.center.x, this.view.center.y);
        this.view.zoom = (this.zoom - zoom.min) / (zoom.max - zoom.min);
    };

    focusEntity = async (entity: Entity, zoom?: number, follow?: boolean, highlight?: boolean) => {
        return new Promise<void>(resolve => {
            // Check debug adn existence
            if (
                this.debugLog(`${follow ? 'Following' : 'Focussing on'} '${entity?.name}' ${entity?.index}`) ||
                !entity
            ) {
                return resolve();
            }

            // Unfollow previously following entity
            this.unfollowEntity();

            // Get duration based on distance
            const position = this.getCenterPosition(entity.cell.x, entity.cell.y, zoom);
            const duration = 0.002 * hypotenuse(position.x - this.x, position.y - this.y);

            // Prepare tween
            const tween = {
                from: this.position.clone(),
                progress: 0,
            };

            // Animate scale
            const scale = zoom ? this.clampZoom(zoom) : this.zoom;
            gsap.to(this.scale, {
                duration,
                ease: 'power4.inOut',
                x: scale,
                y: scale,
                onUpdate: () => {
                    this.zoom = this.scale.x;
                },
            });

            // Animate to entity
            gsap.to(tween, {
                duration,
                ease: 'power4.inOut',
                progress: 1,
                onComplete: () => {
                    // Check follow
                    if (follow) {
                        this.followingEntity = entity;
                        entity.following = true;
                    }

                    // Check highlight
                    if (highlight) {
                        void entity.highlight();
                    }

                    this.updateView();
                    resolve();
                },
                onUpdate: () => {
                    if (entity && !entity.destroyed) {
                        // Move to position
                        const position = this.getCenterPosition(
                            entity.x / setup.map.cells.size,
                            entity.y / setup.map.cells.size,
                            zoom
                        );
                        this.x = tween.progress * (position.x - tween.from.x) + tween.from.x;
                        this.y = tween.progress * (position.y - tween.from.y) + tween.from.y;
                        this.updateView();

                        // Reveal entity
                        const range = entity.size * (highlight ? 1 : 0.5);
                        this.gameState.revealFog(entity.cell, range);
                    }
                },
            });
        });
    };

    unfollowEntity = () => {
        if (this.followingEntity) {
            // Check debug
            if (this.debugLog(`Unfollowing '${this.followingEntity.name}' ${this.followingEntity.index}`)) {
                return;
            }

            // Unfollow
            this.followingEntity.following = false;
            this.followingEntity = null;
        }
    };

    indicateEntity = (entity: Entity, value: string, size = 1) => {
        const { cells } = setup.map;

        // Get indicator or create if needed
        const indicator = this.indicators.children.find(indicator => !indicator.visible) || new PIXI.Text();
        this.indicators.addChild(indicator);
        indicator.style.fill = 0xffffff;
        indicator.style.fontSize = 16 * size;
        indicator.text = value;
        indicator.visible = true;
        indicator.x =
            entity.position.x -
            (value.length * indicator.style.fontSize) / 4 +
            entity.size * cells.size * random(-0.25, 0.25);
        indicator.y =
            entity.position.y - (entity.size * cells.size) / 2 + entity.size * cells.size * random(-0.125, 0.125);

        // Animate indicator
        gsap.to(indicator, {
            duration: 1,
            visible: false,
            y: indicator.y - cells.size / 2,
        });
    };
}
